import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Image, Menu, MenuButton, MenuList, MenuItem, Button } from '@chakra-ui/react';

import flagEn from 'Asset/image/flag/flag-en.svg';
import flagId from 'Asset/image/flag/flag-id.svg';
import { ChevronDownIcon, ChevronUpIcon } from 'Asset/icon';
import { Condition, Iteration } from 'Component/others';
import { TextRegular, TextSemiBold } from 'UiKit/typography';

import styles from './styles.module.css';

export const LANGUAGES = [
  { code: 'en', label: 'English', flag: flagEn },
  { code: 'id', label: 'Bahasa Indonesia', flag: flagId },
];

const useLanguage = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith('id') ? 'id' : 'en';
  const activeLanguage = LANGUAGES.find((lang) => lang.code === currentLang) ?? LANGUAGES[0];
  const changeLanguage = (code) => i18n.changeLanguage(code);

  return { currentLang, activeLanguage, changeLanguage };
};

const DropdownVariant = () => {
  const { currentLang, activeLanguage, changeLanguage } = useLanguage();

  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        variant="ghost"
        borderRadius="8px"
        _hover={{ bg: 'var(--color-primary10, #f0f4f8)' }}
        _active={{ bg: 'var(--color-primary20, #e1e9f0)' }}
        fontWeight="medium"
        fontSize="14px"
        px="8px"
        height="32px"
      >
        <Flex alignItems="center" gap="6px">
          <Image src={activeLanguage.flag} alt={activeLanguage.label} className={styles['language__flag']} />
        </Flex>
      </MenuButton>
      <MenuList minWidth="180px" zIndex={90} py="4px" borderRadius="8px" boxShadow="md">
        <Iteration
          items={LANGUAGES}
          render={(lang) => {
            const isActive = lang.code === currentLang;
            return (
              <MenuItem
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                gap="8px"
                fontWeight={isActive ? 'bold' : 'normal'}
                color={isActive ? 'var(--color-primary70, #3182ce)' : 'inherit'}
              >
                <Image src={lang.flag} alt={lang.label} className={styles['language__flag']} />
                {lang.label}
              </MenuItem>
            );
          }}
        />
      </MenuList>
    </Menu>
  );
};

const InlineVariant = ({ onChange }) => {
  const { currentLang, activeLanguage, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = (code) => {
    changeLanguage(code);
    setIsOpen(false);
    if (onChange) onChange(code);
  };

  return (
    <Box>
      <Flex className={`${styles['inline__row']} ${styles['inline__row--language']}`} onClick={() => setIsOpen((prev) => !prev)}>
        <Box className={styles['inline__label']}>
          <Image src={activeLanguage.flag} alt={activeLanguage.label} className={styles['language__flag']} />
          <TextSemiBold>{activeLanguage.label}</TextSemiBold>
        </Box>
        <Condition statement={isOpen} fallback={<ChevronDownIcon size="16px" />}>
          <ChevronUpIcon size="16px" />
        </Condition>
      </Flex>

      <Condition statement={isOpen}>
        <Iteration
          items={LANGUAGES}
          render={(lang) => {
            const isActive = lang.code === currentLang;
            return (
              <Flex
                key={lang.code}
                className={`${styles['inline__option']} ${isActive ? styles['inline__option--active'] : ''}`}
                onClick={() => handleChange(lang.code)}
              >
                <Image src={lang.flag} alt={lang.label} className={styles['language__flag']} />
                <TextRegular className={isActive ? styles['inline__option-text--active'] : ''}>{lang.label}</TextRegular>
              </Flex>
            );
          }}
        />
      </Condition>
    </Box>
  );
};

const LanguageChanger = ({ variant = 'dropdown', onChange }) => {
  if (variant === 'inline') return <InlineVariant onChange={onChange} />;
  return <DropdownVariant />;
};

export default LanguageChanger;
